import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

export default function BoostOverview() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [period, setPeriod] = useState('this_year');

  const fetchOverview = () => {
    if (!companyId) return;
    setLoading(true);
    const params = new URLSearchParams();
    if (period && period !== 'all') params.append('period', period);

    fetch(`${API}/companies/${companyId}/boosts/overview?${params}`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchOverview(); }, [companyId, period]);

  if (!companyId) return <div style={pageStyle}><p>Please select a company first.</p></div>;
  if (initialLoading) return <div style={pageStyle}><p>Loading...</p></div>;
  if (!data) return <div style={pageStyle}><p>Failed to load overview.</p></div>;

  const statCards = [
    { label: 'Total Boost Amount', value: `$${Number(data.total_boost_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, color: '#3b82f6' },
    { label: 'Total Paid', value: `$${Number(data.total_paid_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, color: '#10b981' },
    { label: 'Total Unpaid', value: `$${Number(data.total_unpaid_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, color: '#f59e0b' },
    { label: 'Active Boosts', value: data.active_boosts || 0, color: '#6366f1' },
    { label: 'Planned', value: data.planned_boosts || 0, color: '#8b5cf6' },
    { label: 'Completed', value: data.completed_boosts || 0, color: '#22c55e' },
    { label: 'Cancelled', value: data.cancelled_boosts || 0, color: '#ef4444' },
  ];

  return (
    <div style={pageStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem', color: '#1e293b' }}>🚀 Boost Management Overview</h1>
        <select value={period} onChange={e => setPeriod(e.target.value)} style={selectStyle}>
          <option value="all">All Time</option>
          <option value="this_month">This Month</option>
          <option value="previous_month">Previous Month</option>
          <option value="this_year">This Year</option>
        </select>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 28 }}>
        {statCards.map((s, i) => (
          <div key={i} style={{ ...cardStyle, borderLeft: `4px solid ${s.color}` }}>
            <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: '1.35rem', fontWeight: 700, color: '#1e293b' }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Pending Actions */}
      {Array.isArray(data.pending_actions) && data.pending_actions.length > 0 && (
        <div style={{ ...cardStyle, borderLeft: '4px solid #f59e0b', marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 10px', fontSize: '1rem', color: '#92400e' }}>⚠️ Pending Actions</h3>
          {data.pending_actions.map((a, i) => (
            <div key={i} style={{ padding: '6px 0', fontSize: '0.88rem', color: '#78350f' }}>• {a.message}</div>
          ))}
        </div>
      )}

      {/* Spend by Platform */}
      {Array.isArray(data.spend_by_platform) && data.spend_by_platform.length > 0 && (
        <div style={{ ...cardStyle, marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 14px', fontSize: '1rem', color: '#1e293b' }}>Spend by Platform</h3>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Platform</th>
                <th style={thStyle}>Boosts</th>
                <th style={thStyle}>Total Amount</th>
                <th style={thStyle}>Paid</th>
              </tr>
            </thead>
            <tbody>
              {data.spend_by_platform.map((p, i) => (
                <tr key={i}>
                  <td style={tdStyle}>{p.platform}</td>
                  <td style={tdStyle}>{p.count}</td>
                  <td style={tdStyle}>${Number(p.total_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  <td style={tdStyle}>${Number(p.paid_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Active Boosts List */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h3 style={{ margin: 0, fontSize: '1rem', color: '#1e293b' }}>Active Boosts</h3>
            <button onClick={() => navigate('/company/boosts/entries')} style={linkBtnStyle}>View All →</button>
          </div>
          {Array.isArray(data.active_boosts_list) && data.active_boosts_list.length > 0 ? (
            data.active_boosts_list.map(b => (
              <div key={b.id} style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9', fontSize: '0.88rem', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#334155' }}>{b.name}</span>
                <span style={{ color: '#64748b' }}>${Number(b.total_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
            ))
          ) : <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No active boosts</p>}
        </div>

        {/* Recent Boosts */}
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 14px', fontSize: '1rem', color: '#1e293b' }}>Recent Boosts</h3>
          {Array.isArray(data.recent_boosts) && data.recent_boosts.length > 0 ? (
            data.recent_boosts.map(b => (
              <div key={b.id} style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9', fontSize: '0.88rem', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#334155' }}>{b.name}</span>
                <span style={{ ...statusBadge(b.status) }}>{b.status}</span>
              </div>
            ))
          ) : <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No recent boosts</p>}
        </div>
      </div>
    </div>
  );
}

const statusBadge = (status) => {
  const colors = { active: '#3b82f6', planned: '#8b5cf6', completed: '#22c55e', cancelled: '#ef4444' };
  return { padding: '2px 8px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600, color: '#fff', background: colors[status] || '#94a3b8' };
};

const pageStyle = { padding: 28, maxWidth: 1200, margin: '0 auto' };
const cardStyle = { background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' };
const selectStyle = { padding: '7px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.88rem', background: '#fff', cursor: 'pointer' };
const linkBtnStyle = { background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 };
const tableStyle = { width: '100%', borderCollapse: 'collapse' };
const thStyle = { textAlign: 'left', padding: '8px 10px', fontSize: '0.78rem', color: '#64748b', borderBottom: '2px solid #e2e8f0', fontWeight: 600 };
const tdStyle = { padding: '8px 10px', fontSize: '0.85rem', color: '#334155', borderBottom: '1px solid #f1f5f9' };
